/**
 * Product Upsells Component
 * Handles toggle mode upsells that should be added with the main product
 * Handles modal display for upsell details
 */

class ProductUpsells {
  constructor(container) {
    this.container = container;
    this.mode = this.container.dataset.upsellMode;
    this.enableModal = this.container.dataset.enableModal === 'true';
    this.isProcessing = false;

    if (this.mode === 'toggle') {
      this.init();
    }

    if (this.enableModal) {
      this.initModal();
    }
  }

  init() {
    // Find the product form
    this.productForm = this.container.closest('[data-section-type="product"]')?.querySelector('form[id^="AddToCartForm"]');

    if (!this.productForm) {
      console.warn('Product form not found for upsell toggles');
      return;
    }

    // Mark form as having upsell handling
    this.productForm.dataset.hasUpsellHandler = 'true';

    // Listen for form submission in CAPTURE phase to run before theme.AjaxProduct
    // Use stopImmediatePropagation to prevent other listeners from running
    this.productForm.addEventListener('submit', this.handleFormSubmit.bind(this), true);
  }

  handleFormSubmit(evt) {
    // Prevent duplicate processing
    if (this.isProcessing) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      return;
    }

    const checkedUpsells = this.getCheckedUpsells();

    if (checkedUpsells.length === 0) {
      // No upsells selected, let form submit normally
      return;
    }

    // Prevent all form submission handlers
    evt.preventDefault();
    evt.stopPropagation();
    evt.stopImmediatePropagation();

    // Add main product and upsells together
    this.addProductsToCart(checkedUpsells);
  }

  getCheckedUpsells() {
    const checkboxes = this.container.querySelectorAll('.js-upsell-toggle:checked');
    return Array.from(checkboxes).map(checkbox => ({
      id: checkbox.dataset.variantId,
      quantity: 1
    }));
  }

  async addProductsToCart(upsells) {
    const addToCartBtn = this.productForm.querySelector('[data-add-to-cart]');

    if (!addToCartBtn || this.isProcessing) return;

    // Set processing flag
    this.isProcessing = true;

    // Show loading state
    addToCartBtn.classList.add('is-loading');
    addToCartBtn.disabled = true;

    try {
      // Get main product form data
      const formData = new FormData(this.productForm);
      const mainProductId = formData.get('id');
      const mainProductQuantity = formData.get('quantity') || 1;

      // Build items array with main product + upsells - ensure IDs are integers
      const items = [
        {
          id: parseInt(mainProductId),
          quantity: parseInt(mainProductQuantity)
        },
        ...upsells.map(upsell => ({
          id: parseInt(upsell.id),
          quantity: parseInt(upsell.quantity)
        }))
      ];

      // Add all items at once using the cart/add.js endpoint
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });

      const data = await response.json();

      if (data.status === 422 || !response.ok) {
        this.handleError(data);
      } else {
        this.handleSuccess(data);
      }
    } catch (error) {
      console.error('Error adding products to cart:', error);
      this.handleError({ description: 'An error occurred. Please try again.' });
    } finally {
      // Remove loading state and reset processing flag
      addToCartBtn.classList.remove('is-loading');
      addToCartBtn.disabled = false;
      this.isProcessing = false;
    }
  }

  handleSuccess(data) {
    // Remove any existing errors
    const errors = this.productForm.querySelector('.errors');
    if (errors) {
      errors.remove();
    }

    // Dispatch success event for cart drawer to open
    this.productForm.dispatchEvent(new CustomEvent('ajaxProduct:added', {
      detail: {
        product: data.items ? data.items[0] : data,
        addToCartBtn: this.productForm.querySelector('[data-add-to-cart]')
      },
      bubbles: true
    }));

    // Reload if on cart page
    if (document.body.classList.contains('template-cart')) {
      window.scrollTo(0, 0);
      location.reload();
    }
  }

  handleError(error) {
    const errorMessage = error.description || error.message || 'An error occurred';

    // Remove existing errors
    const existingErrors = this.productForm.querySelector('.errors');
    if (existingErrors) {
      existingErrors.remove();
    }

    // Create error div
    const errorDiv = document.createElement('div');
    errorDiv.classList.add('errors', 'text-center');
    errorDiv.textContent = errorMessage;
    this.productForm.append(errorDiv);

    // Dispatch error event
    this.productForm.dispatchEvent(new CustomEvent('ajaxProduct:error', {
      detail: { errorMessage },
      bubbles: true
    }));
  }

  initModal() {
    if (!this.enableModal) return;

    this.focusedElementBeforeModal = null;
    this.currentModal = null;

    // Bind methods for event listeners
    this._handleFocusTrap = this._handleFocusTrap.bind(this);
    this._handleEscape = this._handleEscape.bind(this);

    // Handle modal triggers
    const triggers = this.container.querySelectorAll('.js-upsell-modal-trigger');
    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const upsellId = trigger.dataset.upsellId;
        this.openModal(upsellId);
      });
    });

    // Global escape key listener
    document.addEventListener('keydown', this._handleEscape);
  }

  openModal(upsellId) {
    // Find the modal for this specific upsell
    this.currentModal = this.container.querySelector(`[data-upsell-modal="${upsellId}"]`);
    if (!this.currentModal) return;

    // Store currently focused element
    this.focusedElementBeforeModal = document.activeElement;

    // Setup close button listeners for this modal
    const closeButtons = this.currentModal.querySelectorAll('[data-modal-close]');
    closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.closeModal());
    });

    // Show modal
    this.currentModal.hidden = false;
    this.currentModal.setAttribute('aria-hidden', 'false');

    // Trigger reflow before adding class for transition
    this.currentModal.offsetHeight;
    this.currentModal.classList.add('is-active');

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    // Set page content inert
    this._setPageInert(true);

    // Set up focus trap
    document.addEventListener('keydown', this._handleFocusTrap);

    // Focus modal container first, then close button
    this.currentModal.setAttribute('tabindex', '-1');
    this.currentModal.focus();

    // Focus close button
    const closeButton = this.currentModal.querySelector('[data-modal-close]');
    if (closeButton) {
      setTimeout(() => closeButton.focus(), 100);
    }
  }

  closeModal() {
    if (!this.currentModal) return;

    // Remove focus trap
    document.removeEventListener('keydown', this._handleFocusTrap);

    // Remove inert from page content
    this._setPageInert(false);

    this.currentModal.classList.remove('is-active');

    // Wait for animation to complete
    setTimeout(() => {
      this.currentModal.hidden = true;
      this.currentModal.setAttribute('aria-hidden', 'true');
      this.currentModal.removeAttribute('tabindex');

      // Unlock body scroll
      document.body.style.overflow = '';

      // Return focus to trigger element
      if (this.focusedElementBeforeModal && typeof this.focusedElementBeforeModal.focus === 'function') {
        this.focusedElementBeforeModal.focus();
      }

      this.focusedElementBeforeModal = null;
      this.currentModal = null;
    }, 300);
  }

  /**
   * Handle escape key
   */
  _handleEscape(e) {
    if (e.key === 'Escape' && this.currentModal && !this.currentModal.hidden) {
      this.closeModal();
    }
  }

  /**
   * Handle focus trap keyboard events
   */
  _handleFocusTrap(e) {
    if (e.key !== 'Tab' || !this.currentModal) return;

    const focusableElements = this.currentModal.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    const focusableArray = Array.from(focusableElements).filter(el => el.offsetParent !== null);

    if (focusableArray.length === 0) return;

    const firstFocusable = focusableArray[0];
    const lastFocusable = focusableArray[focusableArray.length - 1];

    if (e.shiftKey) {
      // Shift + Tab - moving backwards
      if (document.activeElement === firstFocusable || document.activeElement === this.currentModal) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      // Tab - moving forwards
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  /**
   * Set inert attribute on all page content except the modal
   */
  _setPageInert(inert) {
    const bodyChildren = document.body.children;

    for (let i = 0; i < bodyChildren.length; i++) {
      const child = bodyChildren[i];

      // Skip the modal's container, scripts, and style tags
      if (child.contains(this.currentModal) ||
        child.tagName === 'SCRIPT' ||
        child.tagName === 'STYLE' ||
        child.tagName === 'LINK') {
        continue;
      }

      if (inert) {
        child.setAttribute('inert', '');
        child.setAttribute('aria-hidden', 'true');
      } else {
        child.removeAttribute('inert');
        child.removeAttribute('aria-hidden');
      }
    }
  }
}

// Initialize all product upsell components
function initProductUpsells(container = document) {
  container.querySelectorAll('.component-product-upsells').forEach(element => {
    if (!element.dataset.upsellsInitialized) {
      element.dataset.upsellsInitialized = 'true';
      new ProductUpsells(element);
    }
  });
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  initProductUpsells();
});

// Re-initialize on Shopify section load (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (event) {
    initProductUpsells(event.target);
  });
}
